import {DataTableSortStatus} from 'mantine-datatable';
import {
    sortRecords,
    useTableSort,
} from '@flexinet/ui-components';

import {
    useDeletePaymentMethodsMutation,
    usePaymentMethodsInfiniteQuery,
} from './usePaymentMethodsQuery';
import {usePaymentMethodsSearch} from './usePaymentMethodsSearch';
import {usePaymentMethodsTableColumns} from './usePaymentMethodsTableColumns';
import {PaymentMethodsFilters} from './usePaymentMethodsFilter';
import {PaymentMethod} from '../types';

/**
 * Default sort: NEWEST FIRST, on `createdAtMillis`.
 *
 * The key must be immutable and non-null. `created: Int!` is set by Stripe when
 * the payment method is created and never moves, so it satisfies both.
 */
export const DEFAULT_SORT_STATUS: DataTableSortStatus<PaymentMethod> = {
    columnAccessor: 'createdAtMillis',
    direction: 'desc',
};

/**
 * Which fields a keyword matches against.
 *
 * ⚠️ `last4` is the ONLY card number fragment available here, and it is what an
 * operator has in front of them when a customer reads out a card. The full
 * number, fingerprint and any other PAN data are deliberately NOT matched —
 * `fingerprint` is not even selected by the list query (§11: a value not
 * fetched cannot leak).
 */
const matchesKeyword = (item: PaymentMethod, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    return [
        item.id,
        item.customerId,
        item.type,
        item.card?.brand,
        item.card?.last4,
    ].some(value => typeof value === 'string'
        && value.toLowerCase()
                .includes(needle));
};

/**
 * Whether a record satisfies every active CLIENT-SIDE filter.
 *
 * ⚠️ `type` is excluded here because it is applied SERVER-SIDE — see
 * usePaymentMethodsQuery. Re-applying it client-side would be harmless but
 * misleading: it would suggest the narrowing happens over the loaded window
 * when the server already did it across the whole dataset.
 */
const matchesFilters = (
    item: PaymentMethod,
    filters: PaymentMethodsFilters,
): boolean =>
    Object.entries(filters)
          .filter(([key]) => key !== 'type')
          .every(([key, value]) => {
              if (value === '' || typeof value === 'undefined') {
                  return true;
              }
              const actual = item[key as keyof PaymentMethod];
              return String(actual) === value;
          });

/**
 * FNP-918: the high-level list hook — query + search + filter + sort.
 *
 * ⚠️ SCOPE OF THIS CHANGE. payment-methods is MONEY-PATH ADJACENT: the module
 * renders Stripe Elements and is consumed by payment-intents. Only the
 * LIST/TABLE layer is touched. No payment logic, Element wiring or
 * confirm/capture flow is changed here.
 *
 * ⚠️ SCOPE OF SORT AND KEYWORD: the pages FETCHED SO FAR.
 *
 * `type` is the one narrowing the SERVER performs (PaymentMethodsFilterInput
 * declares `{customerId: String!, type: PaymentMethodType}`), so filtering by
 * type is honest across the whole dataset. The keyword and the sort are not:
 * they run over the loaded window only, because the API takes neither a search
 * term nor a sort argument. Sorting page 1 does not sort the dataset.
 *
 * That is a property of the token-pagination model, not a bug — and it is why
 * the footer states a LOADED count and never a total.
 */
export const usePaymentMethods = (
    customerId?: string,
    filters: PaymentMethodsFilters = {},
) => {
    const query = usePaymentMethodsInfiniteQuery(customerId ?? '', filters);
    const {columns} = usePaymentMethodsTableColumns();
    const {keyword} = usePaymentMethodsSearch();
    const deleteMutation = useDeletePaymentMethodsMutation();

    // Pages are concatenated as they arrive. The type guard matters: a page can
    // be undefined mid-fetch, and a bare flatMap would throw (§9).
    const items: PaymentMethod[] = (query.data?.pages ?? [])
        .flatMap(page => (page && 'items' in page ? page.items : []) ?? [])
        .filter(Boolean)
        // Normalise Stripe's epoch-seconds into millis ONCE, here, so the
        // Created column can both render and sort the same value.
        .map(item => ({
            ...item,
            createdAtMillis: typeof item.created === 'number'
                ? item.created * 1000
                : undefined,
            // ⚠️ FLATTENED FOR SORTING. `sortRecords` reads `record[accessor]`
            // and does NOT resolve a dotted path, so a `card.last4` accessor
            // evaluates to undefined for EVERY row — the column renders
            // clickable and silently does not sort, which §3a calls out as
            // strictly worse than no sorting at all. Verified against
            // @flexinet/ui-components 2.0.2.
            //
            // Copying the value up is a per-entity judgement (WHICH key this
            // table sorts by), not a re-implementation of the comparator — the
            // universal nulls-last/numeric rules still come from sortRecords.
            // A non-card method has no card, so these stay undefined and such
            // rows sort last in both directions, which is correct: "no card" is
            // a state, not a small value.
            //
            // The real fix is dotted-path support in the shared utility; that
            // is a library change (FNP-912's owner), not a module change.
            cardLast4: item.card?.last4,
            cardBrand: item.card?.brand,
            cardExpYear: item.card?.expYear,
        }));

    const needle = keyword.trim()
                          .toLowerCase();

    // Keyword and filters COMPOSE — both are applied, so using them together
    // narrows rather than resets (§7).
    const matched = items.filter(item =>
        matchesKeyword(item, needle) && matchesFilters(item, filters));

    // Sort state is COMPONENT-level (inside this hook's instance), not the
    // module-scoped store: sort is how ONE operator is viewing the table right
    // now, unlike keyword and filters which are shared across siblings (§3a).
    //
    // `useTableSort` is the SHARED comparator (FNP-912) — nulls last in both
    // directions, numbers numerically, strings via localeCompare.
    const {
        sortStatus,
        setSortStatus,
    } = useTableSort<PaymentMethod>(matched, DEFAULT_SORT_STATUS);

    // Sorted separately rather than taking `sorted` from the hook: `matched` is
    // rebuilt every render (new object identities from the map above), and
    // sortRecords is the same comparator applied to the current array.
    const visibleItems = sortRecords(matched, sortStatus);

    return {
        /** The full loaded window BEFORE keyword/filter — the footer's denominator. */
        items,
        /** What the table renders: filtered then sorted. */
        visibleItems,
        columns,
        sortStatus,
        setSortStatus,
        keyword,
        loading: query.isLoading,
        isError: query.isError,
        error: query.error,
        isFetchingMore: query.isFetchingNextPage,
        // The control needs a token-ish value to decide "more available". The
        // query owns the real token; `hasNextPage` is the honest projection of
        // it, and the sentinel is never parsed or sent anywhere.
        nextToken: query.hasNextPage ? 'has-more' : null,
        fetchRecords: () => {
            // hasNextPage is checked BEFORE fetchNextPage (§9).
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
        deleteRecords: (ids?: string[]) => {
            if (ids && ids.length > 0) {
                deleteMutation.mutate(ids);
            }
        },
        deleteMutation,
    };
};
